import { create } from 'zustand';
import type { PatrolLocationWithProfile, Profile } from '@asi/shared';
import { supabase } from '@/services/supabase';

// Maximum age (ms) of `patrol_locations.updated_at` before the row is treated
// as OFFLINE in the UI. Mirrors the constant in `emergency-store.ts` — the
// mobile app only flips the column to OFFLINE on a clean logout, so without
// this guard a crashed/killed app appears AVAILABLE forever.
const PRESENCE_FRESHNESS_MS = 180_000;

interface PatrolsState {
  patrols: PatrolLocationWithProfile[];
  loading: boolean;
  fetchPatrols: (silent?: boolean) => Promise<void>;
  subscribeToPatrolLocations: () => () => void;
}

// Synthetic location row used when a patrol profile exists but has no
// `patrol_locations` entry yet (e.g. just-created user before first GPS
// fix lands). Renders as OFFLINE in the UI but keeps the patrol visible
// in the list so the operator can still call them.
function synthesizeLocation(profile: Profile): PatrolLocationWithProfile {
  return {
    id: `pending-${profile.id}`,
    patrol_id: profile.id,
    location: { lat: 0, lng: 0 },
    heading: null,
    speed: null,
    accuracy: null,
    battery_level: null,
    status: 'OFFLINE',
    updated_at: new Date(0).toISOString(),
    patrol: profile,
  };
}

export const usePatrolsStore = create<PatrolsState>((set, get) => ({
  patrols: [],
  loading: false,

  // Source of truth is `profiles` (every patrol the org has hired). We then
  // left-join `patrol_locations` so patrolers without a GPS fix yet still
  // appear in the list — they were hidden before because the previous query
  // started from `patrol_locations` and the seeded rows have status='OFFLINE'
  // which the .neq filter excluded.
  fetchPatrols: async (silent = false) => {
    if (!silent) set({ loading: true });
    try {
      const [{ data: profiles, error: profErr }, { data: locs, error: locErr }] = await Promise.all([
        supabase
          .from('profiles')
          .select('*')
          .eq('role', 'patrol')
          .eq('is_active', true),
        supabase
          .from('patrol_locations')
          .select('*'),
      ]);

      if (profErr) throw profErr;
      if (locErr) throw locErr;

      const locByPatrol = new Map<string, PatrolLocationWithProfile>();
      (locs ?? []).forEach((l: any) => {
        locByPatrol.set(l.patrol_id, l as PatrolLocationWithProfile);
      });

      const merged: PatrolLocationWithProfile[] = (profiles ?? []).map((p: any) => {
        const loc = locByPatrol.get(p.id);
        if (!loc) return synthesizeLocation(p as Profile);
        // Override stored status with OFFLINE when the row is stale so the
        // operator never sees a phantom AVAILABLE patrol after a crash/kill.
        const ageMs = Date.now() - new Date(loc.updated_at).getTime();
        const stale = ageMs > PRESENCE_FRESHNESS_MS;
        return {
          ...loc,
          status: stale ? 'OFFLINE' : loc.status,
          patrol: p as Profile,
        };
      });

      set({ patrols: merged });
    } catch (err) {
      console.error('[patrols-store] fetchPatrols failed', err);
    } finally {
      if (!silent) set({ loading: false });
    }
  },

  subscribeToPatrolLocations: () => {
    const channel = supabase
      .channel(`patrol-locations-realtime-${Date.now()}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'patrol_locations' },
        () => {
          get().fetchPatrols(true);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },
}));
